import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  AdminOrganizationSubscriptionResponse,
  AdminSubscriptionUpdatePayload
} from '@utils/types/admin';
import { ADMIN_ORG_SUBSCRIPTION_QUERY_KEY } from './use-get-admin-organization-subscription';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type UpdateSubscriptionParams = {
  organizationId: string;
  payload: AdminSubscriptionUpdatePayload;
};

/**
 * Hook to change an organization's plan
 *
 * PUT /api/admin/on-prem/organizations/{id}/subscription
 */
export const useUpdateAdminOrganizationSubscription = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateSubscription = async ({
    organizationId,
    payload
  }: UpdateSubscriptionParams) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/subscription`;
    const response = await Axios.put<AdminOrganizationSubscriptionResponse>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    AdminOrganizationSubscriptionResponse,
    AxiosError<BaseError>,
    UpdateSubscriptionParams
  >(updateSubscription, {
    onSuccess: (_, variables) => {
      toast.success(t('subscription-updated-successfully'));
      queryClient.invalidateQueries([
        ADMIN_ORG_SUBSCRIPTION_QUERY_KEY,
        variables.organizationId
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-update-subscription');
      toast.error(errorMessage);
    }
  });
};
